import axios from "axios";
import { useEffect, useState } from "react";
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation
} from "react-router-dom";

import Login from "./Auth/Login/Login";
import OTP from "./Auth/OTP";
import ResetPassword from "./Auth/ResetPassword";
import SignUp from "./Auth/SignUp/signup";
import Alert from "./components/Alert";
import ErrorBoundary from "./components/ErrorBoundary";
import PageNotFound from "./components/PageNotFound";
import Dashboard from "./Dashboard";
import Home from "./Home";
import AboutUs from "./Home/about-us";
import ConsolidatedSteps from "./Home/consolidated-steps";
import FaqPage from "./Home/faqPage";
import NewsPage from "./Home/newsPage";
import TaxCollection from "./Home/tax-collection";
import TaxLegislation from "./Home/tax-legislation";
import TaxSystem from "./Home/tax-system";
import TaxType from "./Home/tax-type";
import useAutoLogout from "./Hooks/useAutoLogout";
import CorporateIdentityManagement from "./Identity-management-corporate";
import CorporateIdentityManagementSuccess from "./Identity-management-corporate/final";
import IndividualIdentityManagement from "./Identity-management-individual";
import IndividualIdentityManagementSuccess from "./Identity-management-individual/final";
import ETranzact from "./payment/etranzact";
import Flutterwave from "./payment/flutterwave";
import Interswitch from "./payment/interswitchpay";
import MoniePoint from "./payment/moniepoint";
import RemitaCheckout from "./payment/remita";
import Unifiedpayment from "./payment/unifiedpayment";
import TCCVerification from "./TCC-Verification";

function App() {
	const history = useHistory();
	const location = useLocation();
	const accessToken = localStorage.getItem("access_token");
	let userInfo = localStorage.getItem("user_info");

	if (accessToken)
		axios.defaults.headers.common["Authorization"] =
			`Bearer ${accessToken}`;

	if (userInfo) {
		userInfo = JSON.parse(userInfo);
	}

	const [alert, setAlert] = useState({
		showing: false,
		type: null,
		message: ""
	});

	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			if (error?.response) {
				if (error.response?.data?.err?.name === "TokenExpiredError") {
					logout();
				}
			}
			return Promise.reject(error);
		}
	);

	async function logout() {
		try {
			const userId = userInfo?.id;

			await axios.post(`/logout/${userId}`);

			localStorage.removeItem("user_info");
			localStorage.removeItem("access_token");

			history.push("/login", {
				from: location.pathname,
				state: location.state,
				accountType: userInfo?.account_type
			});
		} catch (error) {
			if (error.response) {
				setAlert({
					...alert,
					showing: true,
					type: "error",
					message:
						error.response.data?.msg ||
						error.response.data?.errors?.[0] ||
						error.response.data?.errors?.[0]?.message ||
						error.response.data?.errors?.details[0]?.message ||
						error.response?.data?.message
				});
			} else {
				setAlert({
					...alert,
					showing: true,
					type: "error",
					message: error.message
				});
			}
		}
	}

	useAutoLogout(logout);

	useEffect(() => {
		const delay = setTimeout(
			() =>
				setAlert({
					showing: false,
					type: null,
					message: ""
				}),
			3000
		);

		return () => clearTimeout(delay);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!alert.showing]);

	return (
		<>
			{alert.showing && <Alert text={alert.message} type={alert.type} />}
			<Switch>
				<Route path="/home">
					<Home />
				</Route>
				<Route path="/about-us">
					<AboutUs />
				</Route>
				<Route path="/login">
					<Login />
				</Route>
				<Route path="/reset-password">
					<ResetPassword />
				</Route>
				<Route path="/signup">
					<SignUp />
				</Route>
				<Route path="/otp-phone">
					<OTP title="Phone" />
				</Route>
				<Route path="/otp-email">
					<OTP title="Email" />
				</Route>
				<Route path="/enugutccverification">
					<TCCVerification />
				</Route>
				<Route path="/frequently-asked-questions">
					<FaqPage />
				</Route>
				<Route path="/identity-management">
					{userInfo?.account_type === "corporate" ? (
						<CorporateIdentityManagement />
					) : (
						<IndividualIdentityManagement />
					)}
				</Route>
				<Route path="/individual-identity-management">
					{!/^(individual)|(corporate)$/i.test(
						userInfo?.account_type
					) ? (
						<IndividualIdentityManagement />
					) : (
						<div>You should not be here...</div>
					)}
				</Route>
				<Route path="/corporate-identity-management">
					<CorporateIdentityManagement />
				</Route>
				<Route path="/CIM-success">
					<CorporateIdentityManagementSuccess />
				</Route>
				<Route path="/IM-success">
					<IndividualIdentityManagementSuccess />
				</Route>
				<Route path="/pay/unifiedpayment">
					<Unifiedpayment />
				</Route>
				<Route path="/pay/flutterwave">
					<Flutterwave />
				</Route>
				<Route path="/pay/interswitch">
					<Interswitch />
				</Route>
				<Route path="/pay/moniepoint">
					<MoniePoint />
				</Route>
				<Route path="/pay/remita">
					<RemitaCheckout />
				</Route>
				<Route path="/pay/etranzact">
					<ETranzact />
				</Route>
				<Route path="/404">
					<PageNotFound />
				</Route>
				<Route path="/latest-news">
					<NewsPage />
				</Route>
				<Route path="/tax-type">
					<TaxType />
				</Route>
				<Route path="/tax-system">
					<TaxSystem />
				</Route>
				<Route path="/tax-collection">
					<TaxCollection />
				</Route>
				<Route path="/tax-legislation">
					<TaxLegislation />
				</Route>
				<Route path="/steps-to-pay-bills">
					<ConsolidatedSteps />
				</Route>
				<ErrorBoundary>
					<Route path="/">
						{(userInfo?.account_type &&
							!/^(individual)|(corporate)$/i.test(
								userInfo?.account_type
							)) ||
						userInfo?.anssid ? (
							<Dashboard />
						) : accessToken ? (
							<Redirect to="/identity-management" />
						) : (
							<Redirect to="/home" />
						)}
					</Route>
				</ErrorBoundary>
			</Switch>
		</>
	);
}

export default App;
